"use client";

import { useEffect, useId, useRef, useState, type ReactNode } from "react";
import AccountMenuTriggerButton from "@/components/layout/AccountMenuTriggerButton";
import { Check, Copy } from "@/components/ui/icons";
import { formatUserIdBadge } from "@/lib/format";
import { cn } from "@/lib/utils";
import type { AuthUser } from "@/store/slices/auth.slice";

const flyoutSurfaceClass =
  "theme-menu-surface fixed inset-x-2 top-[calc(env(safe-area-inset-top)+0.75rem)] z-[60] max-h-[calc(100dvh-1.5rem)] overflow-y-auto rounded-[1.35rem] border text-foreground shadow-[0_24px_56px_rgba(15,23,42,0.28)] sm:absolute sm:left-auto sm:right-0 sm:top-[calc(100%+0.72rem)] sm:z-50 sm:max-h-none sm:w-[min(286px,calc(100vw-1.5rem))] sm:max-w-[calc(100vw-1.5rem)] sm:overflow-hidden";
const flyoutHeaderClass =
  "theme-menu-header px-3 py-2 text-[0.72rem] font-bold uppercase tracking-[0.16em] text-muted-foreground";
const idButtonClass =
  "theme-button-secondary inline-flex w-full items-center justify-between gap-3 rounded-[0.95rem] border px-3 py-2.5 text-left font-mono text-[0.75rem] font-semibold tracking-[0.08em] transition hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/20";

type AccountFlyoutShellProps = {
  user: AuthUser;
  variant?: "default" | "session";
  headerEyebrow: string;
  triggerAriaLabel: string;
  triggerTitle?: string;
  triggerIconOnly?: boolean;
  showUserId?: boolean;
  children: (controls: { close: () => void }) => ReactNode;
};

export default function AccountFlyoutShell({
  user,
  variant = "default",
  headerEyebrow,
  triggerAriaLabel,
  triggerTitle,
  triggerIconOnly = false,
  showUserId = true,
  children,
}: AccountFlyoutShellProps) {
  const [open, setOpen] = useState(false);
  const [copiedUserId, setCopiedUserId] = useState<string | null>(null);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const copyTimeoutRef = useRef<number | null>(null);
  const panelId = useId();

  const userLabel = user.user_code ?? undefined;
  const userDisplayName = user.display_name ?? undefined;
  const userId = user.user_id ?? null;
  const headerTitle = userDisplayName || userLabel || userId || "Account";
  const shortUserId = formatUserIdBadge(userId);
  const idCopied = Boolean(userId && copiedUserId === userId);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-theme-menu]")) return;
      setOpen(false);
    };

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      if (rootRef.current?.contains(target)) return;
      if (target instanceof Element && target.closest("[data-theme-menu]")) return;
      setOpen(false);
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      setOpen(false);
      window.setTimeout(() => {
        triggerRef.current?.focus();
      }, 0);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("focusin", handleFocusIn);
    document.addEventListener("keydown", handleKeyDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("focusin", handleFocusIn);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  const handleCopyId = async () => {
    if (!userId) return;

    try {
      await navigator.clipboard.writeText(userId);
      setCopiedUserId(userId);
      if (copyTimeoutRef.current !== null) {
        window.clearTimeout(copyTimeoutRef.current);
      }
      copyTimeoutRef.current = window.setTimeout(() => {
        setCopiedUserId(null);
        copyTimeoutRef.current = null;
      }, 1200);
    } catch (err) {
      console.warn("[AccountFlyoutShell] Failed to copy user id", err);
    }
  };

  const close = () => {
    setOpen(false);
  };

  return (
    <div ref={rootRef} className="relative flex shrink-0 items-center">
      <AccountMenuTriggerButton
        ref={triggerRef}
        variant={variant}
        label={headerTitle}
        expanded={open}
        iconOnly={triggerIconOnly}
        onClick={() => setOpen((current) => !current)}
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        aria-haspopup="dialog"
        aria-label={triggerAriaLabel}
        title={triggerTitle}
      />

      {open ? (
        <div
          id={panelId}
          role="dialog"
          aria-label={`${headerTitle} account menu`}
          className={flyoutSurfaceClass}
        >
          <div className={flyoutHeaderClass}>{headerEyebrow}</div>
          <div className="px-3 pb-3 pt-3">
            <div className="truncate px-1 text-sm font-semibold text-foreground">
              {headerTitle}
            </div>

            {showUserId && userId ? (
              <button
                type="button"
                onClick={handleCopyId}
                className={cn(idButtonClass, "mt-3", idCopied ? "text-primary" : null)}
                title={userId}
                aria-label={`Copy full user ID ${userId}`}
              >
                <span className="inline-flex min-w-0 items-center gap-2">
                  <span className="min-w-0 truncate">{shortUserId}</span>
                </span>
                {idCopied ? (
                  <Check className="size-3" />
                ) : (
                  <Copy className="size-3" />
                )}
              </button>
            ) : null}
          </div>

          {children({ close })}
        </div>
      ) : null}
    </div>
  );
}
